import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router';
import { Search } from 'lucide-react';
import { SortHeader, PlainHeader, type SortDir } from '@/components/list/sort-header';
import {
  FilterChip,
  FilterGroup,
  ClearFiltersButton,
} from '@/components/list/filter-chip';
import {
  formatCalendarAgo,
  formatShortCalendarDate,
  formatShortDate,
} from '@/components/list/format-relative';
import { adminFetch } from '@/api';

interface Group {
  id: string;
  name: string;
  isPersonal: boolean;
  memberCount: number;
  expenseCount: number;
  monthlyBudget: number | null;
  createdAt: string;
  lastActiveAt: string | null;
}

type SortKey = 'name' | 'createdAt' | 'members' | 'expenses' | 'lastActive';
type KindFilter = 'all' | 'personal' | 'shared';
type ActivityFilter = 'all' | 'active_30d' | 'dormant_90d';

export default function GroupsPage() {
  const [groups, setGroups] = useState<Group[]>([]);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<SortKey>('createdAt');
  const [dir, setDir] = useState<SortDir>('desc');
  const [kind, setKind] = useState<KindFilter>('all');
  const [activity, setActivity] = useState<ActivityFilter>('all');

  const fetchGroups = useCallback(async () => {
    const params = new URLSearchParams({ search, sort, dir });
    if (kind !== 'all') params.set('kind', kind);
    if (activity !== 'all') params.set('activity', activity);
    const res = await adminFetch(`/api/groups?${params.toString()}`);
    const data = await res.json();
    setGroups(data.groups);
  }, [search, sort, dir, kind, activity]);

  useEffect(() => {
    fetchGroups();
  }, [fetchGroups]);

  const applySort = (key: SortKey) => {
    if (key === sort) {
      setDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSort(key);
      setDir(key === 'name' ? 'asc' : 'desc');
    }
  };

  const filtersActive =
    kind !== 'all' || activity !== 'all' || search.length > 0;
  const clearFilters = () => {
    setKind('all');
    setActivity('all');
    setSearch('');
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-[22px] font-bold text-white tracking-tight">
          Groups
        </h1>
        <p className="text-zinc-500 text-[13px] mt-0.5">
          {groups.length.toLocaleString('en-IN')} groups
        </p>
      </div>

      <div className="relative max-w-md">
        <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-600" />
        <input
          placeholder="Search by name..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full pl-10 pr-4 py-2.5 rounded-xl bg-white/[0.04] border border-white/[0.06] text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-emerald-500/30 transition-all"
        />
      </div>

      <div className="space-y-2">
        <FilterGroup label="Kind">
          {(['all', 'personal', 'shared'] as const).map((v) => (
            <FilterChip
              key={v}
              label={v.charAt(0).toUpperCase() + v.slice(1)}
              active={kind === v}
              onClick={() => setKind(v)}
            />
          ))}
        </FilterGroup>
        <FilterGroup label="Activity">
          {(['all', 'active_30d', 'dormant_90d'] as const).map((v) => (
            <FilterChip
              key={v}
              label={
                v === 'all'
                  ? 'All'
                  : v === 'active_30d'
                    ? 'Active last 30d'
                    : 'Dormant 90d+'
              }
              active={activity === v}
              onClick={() => setActivity(v)}
            />
          ))}
          {filtersActive && <ClearFiltersButton onClick={clearFilters} />}
        </FilterGroup>
      </div>

      <div className="bg-white/[0.02] border border-white/[0.06] rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-white/[0.06]">
                <th className="px-5 py-3 text-left">
                  <SortHeader
                    label="Group"
                    sortKey="name"
                    activeSort={sort}
                    activeDir={dir}
                    onSort={applySort}
                  />
                </th>
                <th className="px-5 py-3 text-left">
                  <PlainHeader label="Kind" />
                </th>
                <th className="px-5 py-3 text-left">
                  <SortHeader
                    label="Members"
                    sortKey="members"
                    activeSort={sort}
                    activeDir={dir}
                    onSort={applySort}
                  />
                </th>
                <th className="px-5 py-3 text-left">
                  <SortHeader
                    label="Expenses"
                    sortKey="expenses"
                    activeSort={sort}
                    activeDir={dir}
                    onSort={applySort}
                  />
                </th>
                <th className="px-5 py-3 text-left">
                  <PlainHeader label="Budget" />
                </th>
                <th className="px-5 py-3 text-left">
                  <SortHeader
                    label="Last Active"
                    sortKey="lastActive"
                    activeSort={sort}
                    activeDir={dir}
                    onSort={applySort}
                  />
                </th>
                <th className="px-5 py-3 text-left">
                  <SortHeader
                    label="Created"
                    sortKey="createdAt"
                    activeSort={sort}
                    activeDir={dir}
                    onSort={applySort}
                  />
                </th>
              </tr>
            </thead>
            <tbody>
              {groups.length === 0 && (
                <tr>
                  <td
                    colSpan={7}
                    className="px-5 py-10 text-center text-[12px] text-zinc-500"
                  >
                    No groups match the current filters.
                  </td>
                </tr>
              )}
              {groups.map((group) => (
                <tr
                  key={group.id}
                  className="border-b border-white/[0.03] hover:bg-white/[0.02] transition-colors"
                >
                  <td className="px-5 py-3.5">
                    <Link
                      to={`/admin/groups/${group.id}`}
                      className="flex items-center gap-3"
                    >
                      <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-500/15 to-emerald-500/5 flex items-center justify-center border border-emerald-500/10">
                        <span className="text-[11px] font-bold text-emerald-400">
                          {group.name.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <p className="text-[13px] text-white font-medium">
                        {group.name}
                      </p>
                    </Link>
                  </td>
                  <td className="px-5 py-3.5">
                    {group.isPersonal ? (
                      <span className="inline-block px-2 py-0.5 rounded-md bg-zinc-500/10 border border-zinc-500/20 text-zinc-400 text-[10px] uppercase tracking-wider font-semibold">
                        Personal
                      </span>
                    ) : (
                      <span className="inline-block px-2 py-0.5 rounded-md bg-emerald-500/10 border border-emerald-500/20 text-emerald-300 text-[10px] uppercase tracking-wider font-semibold">
                        Shared
                      </span>
                    )}
                  </td>
                  <td className="px-5 py-3.5">
                    <span className="text-[13px] text-zinc-400 font-[family-name:var(--font-jetbrains)]">
                      {group.memberCount}
                    </span>
                  </td>
                  <td className="px-5 py-3.5">
                    <span className="text-[13px] text-zinc-400 font-[family-name:var(--font-jetbrains)]">
                      {group.expenseCount}
                    </span>
                  </td>
                  <td className="px-5 py-3.5">
                    <span className="text-[13px] text-zinc-400 font-[family-name:var(--font-jetbrains)]">
                      {group.monthlyBudget
                        ? `Rs.${(group.monthlyBudget / 1000).toFixed(0)}K`
                        : '—'}
                    </span>
                  </td>
                  <td className="px-5 py-3.5">
                    <span
                      className={`text-[11px] font-[family-name:var(--font-jetbrains)] ${
                        group.lastActiveAt ? 'text-zinc-400' : 'text-zinc-700'
                      }`}
                      title={
                        group.lastActiveAt
                          ? formatShortCalendarDate(group.lastActiveAt)
                          : undefined
                      }
                    >
                      {/* Latest expense date — a calendar day, not a moment. */}
                      {formatCalendarAgo(group.lastActiveAt)}
                    </span>
                  </td>
                  <td className="px-5 py-3.5">
                    <span className="text-[11px] text-zinc-600 font-[family-name:var(--font-jetbrains)]">
                      {formatShortDate(group.createdAt)}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
